<div class="space-y-8">
  <section class="page-section">
    <div class="page-section-header">
      <div>
        <p class="page-section-kicker">Profile</p>
        <h2 class="page-section-title">
          Welcome
          @if (userDetails(); as currentUserDetails) {
            <span>, {{ currentUserDetails.firstName }}</span>
          }
        </h2>
        <p class="page-section-description">
          Keep your personal details and workspace preferences up to date.
        </p>
      </div>
      <button
        class="bg-transparent border border-nav-btn-border text-nav-btn-text hover:bg-nav-btn-hover-bg hover:text-nav-btn-hover-text px-5 py-2 rounded-full inter-500 text-[14px] transition-colors"
        (click)="onClickEditAccountDetails()"
      >
        {{ isEditingAccountDetails() ? 'Save changes' : 'Edit profile' }}
      </button>
    </div>

    @if (userDetails(); as currentUserDetails) {
      <div class="data-panel">
        <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <label class="flex flex-col gap-2">
            <span class="inter-500 text-[14px] text-inverted-neutral">Email</span>
            <input
              [value]="currentUserDetails.email"
              [disabled]="true"
              type="text"
              class="form-control disabled:opacity-60"
            />
          </label>

          <div class="flex flex-col gap-2">
            <span class="inter-500 text-[14px] text-inverted-neutral">
              Email verification
            </span>
            @if (currentUserDetails.emailVerified) {
              <div
                class="form-control flex min-h-[48px] items-center text-status-green"
              >
                Verified
              </div>
            } @else {
              <div class="flex gap-2">
                <div class="form-control flex min-h-[48px] items-center">
                  Not verified
                </div>
                <button
                  type="button"
                  (click)="sendVerificationEmail()"
                  class="shrink-0 rounded-full border border-kek-purple bg-transparent px-4 py-2 text-kek-purple transition-colors inter-500 hover:bg-kek-purple hover:text-white"
                >
                  {{ sendingVerificationEmail() ? 'Sending...' : 'Verify' }}
                </button>
              </div>
            }
          </div>

          <div
            class="lg:col-span-2 border-t border-textbox-border pt-5 mt-1 space-y-4"
          >
            <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div class="inter-500 text-[15px] text-inverted-neutral">
                  Update email address
                </div>
                <div class="inter-300 text-[13px] text-support-text mt-1">
                  Current email: {{ currentUserDetails.email }}
                </div>
              </div>
              <span
                class="w-fit rounded-full border border-textbox-border px-3 py-1 text-[12px] inter-400 text-support-text"
              >
                @switch (emailChangeStep()) {
                  @case ('update') {
                    Update Email
                  }
                  @case ('confirm') {
                    Confirm Code
                  }
                  @case ('saved') {
                    Saved
                  }
                }
              </span>
            </div>

            @if (emailChangeError()) {
              <div
                class="rounded-[8px] border border-red-secondary bg-red-primary px-4 py-3 text-[14px] inter-400 text-red-secondary"
              >
                {{ emailChangeError() }}
              </div>
            }

            @if (emailChangeMessage()) {
              <div
                class="rounded-[8px] border border-status-green/40 bg-status-green/10 px-4 py-3 text-[14px] inter-400 text-status-green"
              >
                {{ emailChangeMessage() }}
              </div>
            }

            @switch (emailChangeStep()) {
              @case ('update') {
                <div class="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                  <label class="flex flex-col gap-2">
                    <span class="inter-500 text-[14px] text-inverted-neutral">
                      New email
                    </span>
                    <input
                      type="email"
                      [ngModel]="newEmail()"
                      (ngModelChange)="newEmail.set($event)"
                      [disabled]="isEmailChangeBusy()"
                      class="form-control disabled:opacity-60"
                      autocomplete="email"
                    />
                  </label>
                  <button
                    type="button"
                    (click)="requestEmailChange()"
                    [disabled]="!canRequestEmailChange()"
                    class="rounded-full border border-kek-purple bg-transparent px-5 py-3 text-[14px] inter-500 text-kek-purple transition-colors hover:bg-kek-purple hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {{ requestingEmailChange() ? 'Sending...' : 'Send code' }}
                  </button>
                </div>
              }
              @case ('confirm') {
                <div class="space-y-4">
                  <div class="inter-300 text-[14px] text-support-text">
                    We sent a verification code to {{ pendingEmail() }}.
                  </div>
                  <div class="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                    <label class="flex flex-col gap-2">
                      <span class="inter-500 text-[14px] text-inverted-neutral">
                        Verification code
                      </span>
                      <input
                        [ngModel]="verificationCode()"
                        (ngModelChange)="verificationCode.set($event)"
                        [disabled]="isEmailChangeBusy()"
                        class="form-control disabled:opacity-60"
                        autocomplete="one-time-code"
                      />
                    </label>
                    <button
                      type="button"
                      (click)="confirmEmailChange()"
                      [disabled]="!canConfirmEmailChange()"
                      class="rounded-full border border-kek-purple bg-transparent px-5 py-3 text-[14px] inter-500 text-kek-purple transition-colors hover:bg-kek-purple hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {{
                        confirmingEmailChange()
                          ? 'Saving...'
                          : 'Confirm and save'
                      }}
                    </button>
                  </div>
                  <div class="flex flex-wrap gap-3">
                    <button
                      type="button"
                      (click)="resendEmailChangeCode()"
                      [disabled]="isEmailChangeBusy()"
                      class="rounded-full border border-nav-btn-border bg-transparent px-4 py-2 text-[14px] inter-500 text-nav-btn-text transition-colors hover:bg-nav-btn-hover-bg hover:text-nav-btn-hover-text disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {{
                        resendingEmailChangeCode()
                          ? 'Sending...'
                          : 'Resend code'
                      }}
                    </button>
                    <button
                      type="button"
                      (click)="changePendingEmail()"
                      [disabled]="isEmailChangeBusy()"
                      class="rounded-full border border-textbox-border bg-transparent px-4 py-2 text-[14px] inter-500 text-support-text transition-colors hover:bg-textbox-fill disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Change email
                    </button>
                  </div>
                </div>
              }
              @case ('saved') {
                <div class="flex flex-wrap items-center gap-3">
                  <div class="inter-300 text-[14px] text-support-text">
                    Your email address has been updated.
                  </div>
                  <button
                    type="button"
                    (click)="resetEmailChangeFlow()"
                    class="rounded-full border border-nav-btn-border bg-transparent px-4 py-2 text-[14px] inter-500 text-nav-btn-text transition-colors hover:bg-nav-btn-hover-bg hover:text-nav-btn-hover-text"
                  >
                    Update again
                  </button>
                </div>
              }
            }
          </div>

          <label class="flex flex-col gap-2">
            <span class="inter-500 text-[14px] text-inverted-neutral">
              First name
            </span>
            <input
              [ngModel]="currentUserDetails.firstName"
              (ngModelChange)="patchUserDetails({ firstName: $event })"
              [disabled]="!isEditingAccountDetails()"
              type="text"
              class="form-control disabled:opacity-60"
            />
          </label>

          <label class="flex flex-col gap-2">
            <span class="inter-500 text-[14px] text-inverted-neutral">
              Last name
            </span>
            <input
              [ngModel]="currentUserDetails.lastName"
              (ngModelChange)="patchUserDetails({ lastName: $event })"
              [disabled]="!isEditingAccountDetails()"
              type="text"
              class="form-control disabled:opacity-60"
            />
          </label>

          <label class="flex flex-col gap-2">
            <span class="inter-500 text-[14px] text-inverted-neutral">
              Password
            </span>
            <input
              [ngModel]="newPassword()"
              (ngModelChange)="newPassword.set($event)"
              placeholder="Leave blank to keep current password"
              [disabled]="!isEditingAccountDetails()"
              type="password"
              class="form-control disabled:opacity-60"
            />
          </label>

          <label class="flex flex-col gap-2">
            <span class="inter-500 text-[14px] text-inverted-neutral">
              Phone number
            </span>
            <input
              id="account-phone-number"
              aria-label="Phone Number"
              data-testid="account-phone-number"
              [ngModel]="currentUserDetails.mobile"
              (ngModelChange)="patchUserDetails({ mobile: $event })"
              [disabled]="!isEditingAccountDetails()"
              type="text"
              class="form-control disabled:opacity-60"
            />
          </label>
        </div>
      </div>
    }
  </section>

  @if (accountDetails && accountDetails.organisation) {
    <section class="page-section">
      <div class="page-section-header">
        <div>
          <p class="page-section-kicker">Organisation</p>
          <h2 class="page-section-title">Organisation details</h2>
          <p class="page-section-description">
            Read-only account information for your current organisation.
          </p>
        </div>
      </div>

      <div class="data-panel">
        <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <label class="flex flex-col gap-2 lg:col-span-2">
            <span class="inter-500 text-[14px] text-inverted-neutral">
              Organisation name
            </span>
            <input
              [(ngModel)]="accountDetails.organisation.name"
              [disabled]="true"
              type="text"
              class="form-control disabled:opacity-60"
            />
          </label>

          <label class="flex flex-col gap-2">
            <span class="inter-500 text-[14px] text-inverted-neutral">
              Contact email
            </span>
            <input
              [(ngModel)]="accountDetails.organisation.contactEmailAddress"
              [disabled]="true"
              type="text"
              class="form-control disabled:opacity-60"
            />
          </label>

          <label class="flex flex-col gap-2">
            <span class="inter-500 text-[14px] text-inverted-neutral">
              Contact mobile
            </span>
            <input
              [(ngModel)]="accountDetails.organisation.contactMobileNumber"
              [disabled]="true"
              type="text"
              class="form-control disabled:opacity-60"
            />
          </label>
        </div>
      </div>
    </section>
  }

  @if (canDeactivateAccount()) {
    <section class="page-section">
      <div class="page-section-header">
        <div>
          <p class="page-section-kicker">Account access</p>
          <h2 class="page-section-title">Deactivate account</h2>
          <p class="page-section-description">
            Deactivate this account, remove access, and mask selected account
            contact details. Questionnaire data, files, and audit records are
            retained.
          </p>
        </div>
        <button
          type="button"
          (click)="deactivateAccount()"
          [disabled]="deactivatingAccount()"
          class="rounded-full border border-red-secondary bg-red-primary px-5 py-2 inter-500 text-[14px] text-red-secondary transition-colors hover:bg-red-secondary hover:text-red-primary disabled:cursor-not-allowed disabled:opacity-50"
        >
          {{ deactivatingAccount() ? 'Deactivating...' : 'Deactivate account' }}
        </button>
      </div>

      @if (deactivationError()) {
        <div
          class="rounded-[8px] border border-red-secondary bg-red-primary px-4 py-3 text-[14px] inter-400 text-red-secondary"
        >
          {{ deactivationError() }}
        </div>
      }
    </section>
  }

  <section class="page-section">
    <div class="page-section-header">
      <div>
        <p class="page-section-kicker">Preferences</p>
        <h2 class="page-section-title">Workspace preferences</h2>
        <p class="page-section-description">
          Choose interface preferences and notification defaults for this
          account.
        </p>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <div class="data-panel">
        <div class="inter-500 text-[14px] text-inverted-neutral">Time zone</div>
        <div class="mt-2 text-[15px] text-support-text inter-300">
          (UTC+12) New Zealand Standard Time
        </div>
        <button
          type="button"
          class="mt-4 rounded-full border border-nav-btn-border bg-transparent px-4 py-2 text-nav-btn-text transition-colors inter-500 hover:bg-nav-btn-hover-bg hover:text-nav-btn-hover-text"
          (click)="showComingSoonToast()"
        >
          Edit time zone
        </button>
      </div>

      <div class="data-panel">
        <div class="inter-500 text-[14px] text-inverted-neutral">
          Response alerts
        </div>
        <div class="mt-4 space-y-4">
          <div class="flex items-center justify-between gap-4">
            <span class="text-[15px] text-support-text inter-300">
              Existing questionnaires
            </span>
            <label class="relative inline-flex cursor-pointer items-center">
              <input disabled type="checkbox" class="peer sr-only" />
              <span class="settings-toggle-track"></span>
            </label>
          </div>
          <div class="flex items-center justify-between gap-4">
            <span class="text-[15px] text-support-text inter-300">
              New questionnaires
            </span>
            <label class="relative inline-flex cursor-pointer items-center">
              <input disabled type="checkbox" class="peer sr-only" />
              <span class="settings-toggle-track"></span>
            </label>
          </div>
        </div>
      </div>

      <label class="data-panel flex flex-col gap-2">
        <span class="inter-500 text-[14px] text-inverted-neutral">Theme</span>
        <select
          id="theme-toggle"
          class="form-control"
          (change)="onThemeChange($event)"
          [value]="theme()"
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </select>
      </label>
    </div>
  </section>

  @if (accountDetails && accountDetails.organisation) {
    <section class="page-section">
      <div class="page-section-header">
        <div>
          <p class="page-section-kicker">Access</p>
          <h2 class="page-section-title">Organisation users</h2>
          <p class="page-section-description">
            Review the people who can access this account.
          </p>
        </div>
        <button
          type="button"
          (click)="addNewUser()"
          class="bg-transparent border border-nav-btn-border text-nav-btn-text hover:bg-nav-btn-hover-bg hover:text-nav-btn-hover-text px-5 py-2 rounded-full inter-500 text-[14px] transition-colors"
        >
          Add new user
        </button>
      </div>

      <div class="data-table-shell">
        <div class="data-table-header">
          <div class="data-header-cell w-1/4 pl-4">Role</div>
          <div class="data-column-divider"></div>
          <div class="data-header-cell w-1/4 pl-4">Name</div>
          <div class="data-column-divider"></div>
          <div class="data-header-cell w-1/4 pl-4">Email</div>
          <div class="data-column-divider"></div>
          <div class="data-header-cell w-1/4 pl-4">Last login</div>
        </div>
        <div class="data-table-rule"></div>
        @for (user of accountDetails.users; track user) {
          <div class="interactive-row flex py-3 text-[14px] text-support-text inter-300">
            <span class="w-1/4 px-4 break-all">{{ user.role | camelCaseSplit }}</span>
            <span class="w-1/4 px-4 break-all">
              {{ user.firstName + ' ' + user.lastName }}
            </span>
            <span class="w-1/4 px-4 break-all">{{ user.email }}</span>
            <span class="w-1/4 px-4 break-all">
              {{ user.lastLoginAt | date:'dd/MM/yyyy' }}
            </span>
          </div>
        }
      </div>
    </section>
  }
</div>
